import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query"
import type { AxiosResponse } from "axios"

import { useAuthStore } from "@/modules/helpers/hooks/shared"
import { sendHaptic } from "@/modules/helpers/utils"
import type { PagingSchema, SchemaResponse } from "@/modules/types/common/schema"

type UseInfinitePageQueryParams<_TQueryFnData, TItem> = {
  queryKey: string[]
  queryFn: (params: any) => Promise<AxiosResponse<SchemaResponse<PagingSchema<TItem>>>>
  params?: any
  enabled?: boolean
  initialPage?: number
}

export const useInfinitePageQuery = <TQueryFnData, TItem>({
  queryKey,
  queryFn,
  params,
  enabled = true,
  initialPage = 1
}: UseInfinitePageQueryParams<TQueryFnData, TItem>) => {
  const { accessToken } = useAuthStore()
  const queryClient = useQueryClient()

  const hook = useInfiniteQuery({
    queryKey: [...queryKey, accessToken],
    enabled,
    queryFn: ({ pageParam: page = initialPage }) =>
      queryFn({
        ...params,
        page
      }),
    initialPageParam: initialPage,
    getNextPageParam: (lastPage: any) => {
      const hasNext = lastPage?.data?.data?.hasNext
      const currentPage = lastPage?.data?.data?.page || initialPage
      const total = lastPage?.data?.data?.total
      const take = lastPage?.data?.data?.take || params?.take || 10

      // If API provides hasNext, use it
      if (hasNext !== undefined) {
        return hasNext ? currentPage + 1 : undefined
      }

      // Otherwise, calculate based on total and current page
      if (total !== undefined && take) {
        const totalPages = Math.ceil(total / take)
        return currentPage < totalPages ? currentPage + 1 : undefined
      }

      // Fallback: if no hasNext and no total, check if last page has items
      const list = lastPage?.data?.data?.list || []
      return list.length > 0 ? currentPage + 1 : undefined
    },
    select: (data) => {
      const list = data.pages.reduce((acc: any, cur) => {
        return [...acc, ...(cur?.data?.data?.list || [])]
      }, [] as TItem[]) as TItem[]

      const lastPage = data.pages[data.pages.length - 1]
      const currentPage = lastPage?.data?.data?.page || initialPage
      const total = data.pages[0]?.data?.data?.total
      const take = lastPage?.data?.data?.take || params?.take || 10
      const hasNext = lastPage?.data?.data?.hasNext

      // Calculate hasNext if not provided by API
      let calculatedHasNext = hasNext
      if (calculatedHasNext === undefined && total !== undefined && take) {
        const totalPages = Math.ceil(total / take)
        calculatedHasNext = currentPage < totalPages
      }

      return {
        list,
        total,
        page: currentPage,
        hasNext: calculatedHasNext,
        totalPages: total !== undefined && take ? Math.ceil(total / take) : undefined
      }
    }
  })

  const forceRefetch = async () => {
    queryClient.removeQueries({ queryKey: [...queryKey, accessToken] })
    hook.refetch()
    sendHaptic()
  }

  return {
    ...hook,
    forceRefetch
  }
}
